import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  History,
  ClipboardCheck,
  Upload,
  Link2,
  ExternalLink,
  Wand2,
  Paperclip,
  Check,
  X,
  Sparkles,
  Eye,
  ChevronRight,
  ShieldOff,
  Settings,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  useClauseStatusDetail,
  useClauseEvidence,
  useClauseAuditHistory,
  useClauseActions,
  useUpdateClauseStatus,
  useCreateEvidence,
  useConfirmEvidence,
  useDismissEvidence,
} from '@/hooks/use-clause-status';
import { usePlatformModules } from '@/hooks/use-modules';
import { getModuleDisplayName } from '@/lib/modules';
import type {
  ClauseStatusValue,
  ComplianceEvidence,
  CorrectiveAction,
  CriticalityLevel,
  RemediationHint,
} from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';
import { MarkCompliantButton } from '@/app/pages/compliance/components/mark-compliant-button';
import { AttachRecordPicker } from './attach-record-picker';
import {
  StatusPill,
  ScopeTagsBadge,
  needsManualReview,
} from '@/app/pages/compliance/components/compliance-status';
import { resolveMediaUrl } from '@/lib/api-client';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';

// ---------------------------------------------------------------------------
// Shared UI helpers
// ---------------------------------------------------------------------------

const CRITICALITY_CONFIG: Record<
  CriticalityLevel,
  { label: string; bg: string; text: string }
> = {
  low: { label: 'Low', bg: 'bg-blue-50', text: 'text-blue-700' },
  medium: { label: 'Medium', bg: 'bg-yellow-50', text: 'text-yellow-700' },
  high: { label: 'High', bg: 'bg-orange-50', text: 'text-orange-700' },
  critical: { label: 'Critical', bg: 'bg-red-50', text: 'text-red-700' },
};

function CriticalityPill({ level }: { level: CriticalityLevel }) {
  const cfg = CRITICALITY_CONFIG[level];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function actionUrl(hint: RemediationHint, clauseRef: string): string | null {
  if (!hint.route) return null;

  const params = new URLSearchParams();
  if (
    hint.kind === 'missing_record' ||
    hint.kind === 'missing_role' ||
    hint.kind === 'incomplete_record' ||
    hint.kind === 'missing_document'
  ) {
    params.set('clause_ref', clauseRef);
  }
  if (hint.kind === 'missing_document') {
    const documentType =
      (hint.context?.document_type as string) ?? hint.record_type ?? '';
    if (documentType) {
      params.set('document_type', documentType);
    }
  }

  if (!params.toString()) return hint.route;
  const separator = hint.route.includes('?') ? '&' : '?';
  return `${hint.route}${separator}${params.toString()}`;
}

function findFallbackHint(
  primary: RemediationHint,
  hints: RemediationHint[],
  startIndex: number,
  consumed: Set<number>,
): RemediationHint | null {
  const recordType = primary.record_type;
  if (!recordType) return null;

  const candidates: number[] = [];
  for (let idx = startIndex; idx < hints.length; idx++) {
    const h = hints[idx];
    if (h.kind !== 'missing_document' || !h.is_fallback) break;
    if (consumed.has(idx)) continue;

    const fallbackFor = h.context?.for_record_type;
    if (fallbackFor === recordType) {
      // Auto-generated fallback for this primary; keep scanning in case a
      // hand-authored fallback immediately follows it.
      candidates.push(idx);
      continue;
    }
    if (fallbackFor === undefined || fallbackFor === null) {
      // Hand-authored (or unlabelled) fallback; prefer it and stop.
      candidates.push(idx);
      break;
    }
    // Fallback belongs to a different record type; stop scanning.
    break;
  }

  if (candidates.length === 0) return null;
  candidates.forEach((idx) => consumed.add(idx));
  // Return the last candidate (hand-authored if present, otherwise generated).
  return hints[candidates[candidates.length - 1]];
}

function RemediationActionsList({
  hints,
  clauseRef,
  onOpenEvidence,
  isModuleEnabled,
  isLoadingModules,
  modulesError,
  modules,
}: {
  hints: RemediationHint[];
  clauseRef: string;
  onOpenEvidence: () => void;
  isModuleEnabled: (key: string) => boolean;
  isLoadingModules?: boolean;
  modulesError?: Error | null;
  modules?: Record<string, boolean> | null;
}) {
  const { isAdmin } = usePermissions();

  if (isLoadingModules) {
    return <div className="text-sm text-gray-500">Loading module settings…</div>;
  }

  const moduleUnknown = !!modulesError || !modules;

  const moduleUnavailableText = (moduleKey: string | null): string => {
    if (!moduleKey) return 'This action is not available. Upload a document as evidence.';
    const name = getModuleDisplayName(moduleKey);
    if (modulesError) {
      return `Could not load module settings. Enable ${name} in Settings, or upload a document as evidence.`;
    }
    return `${name} is not enabled for your organization. Enable it in Settings, or upload a document as evidence.`;
  };

  const linkClass =
    'inline-flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors';
  const primaryClass = 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100';
  const secondaryClass = 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50';
  const buttonClass =
    'inline-flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50';
  const moduleDisabledClass =
    'rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800';

  function renderFallbackAction(
    fallbackHint: RemediationHint,
    fallbackUrl: string | null,
    actionKey: string,
    testId: string,
  ) {
    if (fallbackUrl) {
      return (
        <Link
          key={actionKey}
          to={fallbackUrl}
          className={`${linkClass} ${secondaryClass}`}
          data-testid={testId}
        >
          <span className="inline-flex items-center gap-2">
            <Upload className="h-4 w-4 flex-shrink-0" />
            {fallbackHint.action_label}
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
        </Link>
      );
    }
    return (
      <button
        key={actionKey}
        type="button"
        onClick={onOpenEvidence}
        className={buttonClass}
        data-testid={testId}
      >
        <span className="inline-flex items-center gap-2">
          <Upload className="h-4 w-4 flex-shrink-0" />
          {fallbackHint.action_label}
        </span>
        <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
      </button>
    );
  }

  const actions: React.ReactNode[] = [];
  const consumedFallbacks = new Set<number>();

  if (moduleUnknown) {
    actions.push(
      <div key="module-error" className={moduleDisabledClass}>
        <ShieldOff className="mr-1 inline h-3 w-3" />
        Could not load module settings. Module-gated links may be unavailable if the module is disabled.
      </div>,
    );
  }

  hints.forEach((hint, index) => {
    if (consumedFallbacks.has(index) || hint.is_fallback) return;

    const url = actionUrl(hint, clauseRef);
    const moduleEnabled = !hint.requires_module || moduleUnknown || isModuleEnabled(hint.requires_module);

    if (hint.kind === 'missing_record' || hint.kind === 'missing_role') {
      const fallbackHint = findFallbackHint(hint, hints, index + 1, consumedFallbacks);
      const fallbackUrl = fallbackHint ? actionUrl(fallbackHint, clauseRef) : null;

      if (!moduleEnabled) {
        actions.push(
          <div key={`hint-${index}`} className="space-y-2">
            <div className={moduleDisabledClass} data-testid={`remediation-action-${index}`}>
              <ShieldOff className="mr-1 inline h-3 w-3" />
              {moduleUnavailableText(hint.requires_module)}
            </div>
            {fallbackHint &&
              renderFallbackAction(
                fallbackHint,
                fallbackUrl,
                `hint-${index}-fallback`,
                `remediation-action-${index}-fallback`,
              )}
            {isAdmin && (
              <Link
                to="/settings/modules"
                className={`${linkClass} ${secondaryClass}`}
              >
                <span className="inline-flex items-center gap-2">
                  <Settings className="h-4 w-4 flex-shrink-0" />
                  Manage modules
                </span>
                <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
              </Link>
            )}
          </div>,
        );
        return;
      }

      if (url) {
        actions.push(
          <Link
            key={`hint-${index}`}
            to={url}
            className={`${linkClass} ${primaryClass}`}
            data-testid={`remediation-action-${index}`}
          >
            <span className="inline-flex items-center gap-2">
              <ExternalLink className="h-4 w-4 flex-shrink-0" />
              {hint.action_label}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
          </Link>,
        );
      } else {
        actions.push(
          <div
            key={`hint-${index}`}
            className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
          >
            {hint.action_label}
          </div>,
        );
      }

      if (fallbackHint) {
        actions.push(
          renderFallbackAction(
            fallbackHint,
            fallbackUrl,
            `hint-${index}-fallback`,
            `remediation-action-${index}-fallback`,
          ),
        );
      }
      return;
    }

    if (hint.kind === 'incomplete_record' || hint.kind === 'child_dependency') {
      const fallbackHint = findFallbackHint(hint, hints, index + 1, consumedFallbacks);
      const fallbackUrl = fallbackHint ? actionUrl(fallbackHint, clauseRef) : null;

      if (!moduleEnabled) {
        actions.push(
          <div key={`hint-${index}`} className="space-y-2">
            <div className={moduleDisabledClass} data-testid={`remediation-action-${index}`}>
              <ShieldOff className="mr-1 inline h-3 w-3" />
              {moduleUnavailableText(hint.requires_module)}
              {isAdmin && (
                <Link to="/settings/modules" className="ml-1 underline">
                  Manage modules
                </Link>
              )}
            </div>
            {fallbackHint &&
              renderFallbackAction(
                fallbackHint,
                fallbackUrl,
                `hint-${index}-fallback`,
                `remediation-action-${index}-fallback`,
              )}
          </div>,
        );
        return;
      }

      if (url) {
        actions.push(
          <Link
            key={`hint-${index}`}
            to={url}
            className={`${linkClass} ${secondaryClass}`}
            data-testid={`remediation-action-${index}`}
          >
            <span className="inline-flex items-center gap-2">
              <ExternalLink className="h-4 w-4 flex-shrink-0" />
              {hint.action_label}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
          </Link>,
        );
      }

      if (fallbackHint) {
        actions.push(
          renderFallbackAction(
            fallbackHint,
            fallbackUrl,
            `hint-${index}-fallback`,
            `remediation-action-${index}-fallback`,
          ),
        );
      }
      return;
    }

    if (hint.kind === 'missing_document') {
      const fallbackHint = findFallbackHint(hint, hints, index + 1, consumedFallbacks);
      const fallbackUrl = fallbackHint ? actionUrl(fallbackHint, clauseRef) : null;

      if (!moduleEnabled) {
        actions.push(
          <div key={`hint-${index}`} className="space-y-2">
            <div className={moduleDisabledClass} data-testid={`remediation-action-${index}`}>
              <ShieldOff className="mr-1 inline h-3 w-3" />
              {moduleUnavailableText(hint.requires_module)}
            </div>
            {fallbackHint &&
              renderFallbackAction(
                fallbackHint,
                fallbackUrl,
                `hint-${index}-fallback`,
                `remediation-action-${index}-fallback`,
              )}
            {isAdmin && (
              <Link
                to="/settings/modules"
                className={`${linkClass} ${secondaryClass}`}
              >
                <span className="inline-flex items-center gap-2">
                  <Settings className="h-4 w-4 flex-shrink-0" />
                  Manage modules
                </span>
                <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
              </Link>
            )}
          </div>,
        );
        return;
      }

      if (url) {
        actions.push(
          <Link
            key={`hint-${index}`}
            to={url}
            className={`${linkClass} ${primaryClass}`}
            data-testid={`remediation-action-${index}`}
          >
            <span className="inline-flex items-center gap-2">
              <Upload className="h-4 w-4 flex-shrink-0" />
              {hint.action_label}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
          </Link>,
        );
      } else {
        actions.push(
          <button
            key={`hint-${index}`}
            type="button"
            onClick={onOpenEvidence}
            className={buttonClass}
            data-testid={`remediation-action-${index}`}
          >
            <span className="inline-flex items-center gap-2">
              <Upload className="h-4 w-4 flex-shrink-0" />
              {hint.action_label}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
          </button>,
        );
      }

      if (fallbackHint) {
        actions.push(
          renderFallbackAction(
            fallbackHint,
            fallbackUrl,
            `hint-${index}-fallback`,
            `remediation-action-${index}-fallback`,
          ),
        );
      }
      return;
    }

    if (hint.kind === 'manual_attestation') {
      actions.push(
        <div
          key={`hint-${index}`}
          className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800"
          data-testid={`remediation-action-${index}`}
        >
          <Eye className="mr-1 inline h-3 w-3" />
          {typeof hint.context?.message === 'string'
            ? hint.context.message
            : 'This clause requires a manual attestation. Use the Override Status card below.'}
        </div>,
      );
    }
  });

  // Render any fallback hints that were not consumed by a preceding primary.
  hints.forEach((hint, index) => {
    if (consumedFallbacks.has(index) || !hint.is_fallback || hint.kind !== 'missing_document') return;
    const fallbackUrl = actionUrl(hint, clauseRef);
    actions.push(
      renderFallbackAction(
        hint,
        fallbackUrl,
        `hint-${index}-unconsumed`,
        `remediation-action-${index}`,
      ),
    );
  });

  return <div className="space-y-2">{actions}</div>;
}

function SuggestedActionsCard({
  hints,
  clauseRef,
  status,
  onOpenEvidence,
  ...moduleProps
}: {
  hints: RemediationHint[];
  clauseRef: string;
  status: ClauseStatusValue;
  onOpenEvidence: () => void;
  isModuleEnabled: (key: string) => boolean;
  isLoadingModules?: boolean;
  modulesError?: Error | null;
  modules?: Record<string, boolean> | null;
}) {
  if (status === 'not_applicable' || status === 'compliant') return null;
  if (hints.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Suggested actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <RemediationActionsList
          hints={hints}
          clauseRef={clauseRef}
          onOpenEvidence={onOpenEvidence}
          {...moduleProps}
        />
      </CardContent>
    </Card>
  );
}

function AdvisoryCard({
  hints,
  clauseRef,
  status,
  onOpenEvidence,
  ...moduleProps
}: {
  hints: RemediationHint[];
  clauseRef: string;
  status: ClauseStatusValue;
  onOpenEvidence: () => void;
  isModuleEnabled: (key: string) => boolean;
  isLoadingModules?: boolean;
  modulesError?: Error | null;
  modules?: Record<string, boolean> | null;
}) {
  if (status !== 'compliant') return null;
  // Manual-attestation hints are not a concrete engine disagreement; they
  // just mean the clause cannot be auto-assessed. Don't frame them as a
  // mismatch in the advisory card.
  const advisoryHints = hints.filter((hint) => hint.kind !== 'manual_attestation');
  if (advisoryHints.length === 0) return null;

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-blue-900">The automated check disagrees</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-blue-800">
          You've recorded this clause as Compliant, and that's what counts toward your score. The automated check reached a different conclusion — here's what it looked for and didn't find.
        </p>
        <RemediationActionsList
          hints={advisoryHints}
          clauseRef={clauseRef}
          onOpenEvidence={onOpenEvidence}
          {...moduleProps}
        />
      </CardContent>
    </Card>
  );
}

type TabId = 'requirement' | 'evidence' | 'audit-history' | 'actions';

const TABS: Array<{ id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'requirement', label: 'Requirement', icon: FileText },
  { id: 'evidence', label: 'Evidence', icon: Paperclip },
  { id: 'audit-history', label: 'Audit History', icon: History },
  { id: 'actions', label: 'Actions', icon: ClipboardCheck },
];

// ---------------------------------------------------------------------------
// Requirement tab
// ---------------------------------------------------------------------------

function RequirementTab({
  requirementSummary,
  guidanceSummary,
}: {
  requirementSummary: string;
  guidanceSummary: string;
}) {
  if (!requirementSummary && !guidanceSummary) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
        <FileText className="mx-auto h-12 w-12 text-gray-300" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No requirement details</h3>
        <p className="mt-1 text-sm text-gray-500">
          Requirement and guidance summaries have not been added to this clause yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {requirementSummary && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">
            Requirement
          </h3>
          <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
            {requirementSummary}
          </div>
        </div>
      )}
      {guidanceSummary && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">
            Guidance
          </h3>
          <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap">
            {guidanceSummary}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence tab
// ---------------------------------------------------------------------------

function validityBadge(evidence: ComplianceEvidence, today: string) {
  if (!evidence.valid_until) return null;
  const daysUntil = differenceInCalendarDays(
    parseISO(evidence.valid_until),
    parseISO(today)
  );

  if (daysUntil < 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        Expired
      </span>
    );
  }
  if (daysUntil <= 30) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
        Expiring soon
      </span>
    );
  }
  return null;
}

function EvidenceTab({ clauseStatusId }: { clauseStatusId: number }) {
  const timeZone = useOrgTimeZone();
  const today = useOrgToday();
  const { data: evidence, isLoading } = useClauseEvidence(clauseStatusId);
  const createEvidence = useCreateEvidence(clauseStatusId);
  const confirmEvidence = useConfirmEvidence(clauseStatusId);
  const dismissEvidence = useDismissEvidence(clauseStatusId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showAttachRecord, setShowAttachRecord] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    createEvidence.mutate({
      evidence_type: 'document',
      title: file.name,
      file,
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddLink = () => {
    if (!linkUrl.trim() || !linkTitle.trim()) return;
    createEvidence.mutate(
      {
        evidence_type: 'external_link',
        title: linkTitle.trim(),
        external_url: linkUrl.trim(),
      },
      {
        onSuccess: () => {
          setLinkUrl('');
          setLinkTitle('');
          setShowLinkForm(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          data-testid="evidence-tab-attach"
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Upload className="h-4 w-4" />
          Attach document
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileUpload}
        />
        <button
          onClick={() => setShowLinkForm(!showLinkForm)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Link2 className="h-4 w-4" />
          Add external link
        </button>
        <button
          type="button"
          onClick={() => setShowAttachRecord(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Link2 className="h-4 w-4" />
          Attach record
        </button>
      </div>

      {showAttachRecord && (
        <AttachRecordPicker
          clauseStatusId={clauseStatusId}
          isOpen={showAttachRecord}
          onClose={() => setShowAttachRecord(false)}
        />
      )}

      {/* External link form */}
      {showLinkForm && (
        <Card>
          <CardContent className="space-y-3 py-3">
            <input
              type="text"
              placeholder="Link title"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="url"
              placeholder="https://…"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddLink}
                disabled={createEvidence.isPending}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {createEvidence.isPending ? 'Saving…' : 'Save link'}
              </button>
              <button
                onClick={() => setShowLinkForm(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evidence list */}
      {!evidence || evidence.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <Paperclip className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No evidence attached</h3>
          <p className="mt-1 text-sm text-gray-500">
            Upload documents, attach records, or add external links as evidence for this clause.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          {evidence.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 px-4 py-3 ${item.is_proposed ? 'bg-amber-50/50' : ''}`}
            >
              {item.evidence_type === 'external_link' ? (
                <ExternalLink className="h-5 w-5 flex-shrink-0 text-blue-500" />
              ) : (
                <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">
                  {item.title}
                </p>
                <p className="text-xs text-gray-500">
                  {item.evidence_type_display} · Added{' '}
                  {formatDateTimeInTimeZone(item.collected_at, timeZone, { dateStyle: 'medium' })}
                  {item.collected_by_username && ` by ${item.collected_by_username}`}
                </p>
              </div>
              {item.is_proposed && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  <Sparkles className="h-3 w-3" />
                  Proposed
                </span>
              )}
              {validityBadge(item, today)}
              {item.evidence_type === 'document' &&
                item.source_model === 'generatedcompliancedocument' &&
                item.source_object_id && (
                <>
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                    <Wand2 className="h-3 w-3" />
                    Wizard-generated
                  </span>
                  <Link
                    to={`/compliance/generated-documents`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    <FileText className="h-4 w-4" />
                    Open document
                  </Link>
                </>
              )}
              {item.external_url && (
                <a
                  href={item.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              {item.file && (
                <a
                  href={resolveMediaUrl(item.file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              {item.is_proposed && (
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    onClick={() => confirmEvidence.mutate(item.id)}
                    disabled={confirmEvidence.isPending}
                    className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    title="Confirm evidence"
                  >
                    <Check className="h-3 w-3" />
                    Confirm
                  </button>
                  <button
                    onClick={() => dismissEvidence.mutate(item.id)}
                    disabled={dismissEvidence.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    title="Dismiss evidence"
                  >
                    <X className="h-3 w-3" />
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit History tab
// ---------------------------------------------------------------------------

function AuditHistoryTab({ clauseRef }: { clauseRef: string }) {
  const { data: responses, isLoading } = useClauseAuditHistory(clauseRef);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  if (!responses || responses.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
        <History className="mx-auto h-12 w-12 text-gray-300" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No audit history</h3>
        <p className="mt-1 text-sm text-gray-500">
          No audit responses have been recorded for this clause yet.
        </p>
      </div>
    );
  }

  const RESPONSE_COLORS: Record<string, string> = {
    yes: 'text-green-700 bg-green-50',
    no: 'text-red-700 bg-red-50',
    partial: 'text-amber-700 bg-amber-50',
    not_applicable: 'text-gray-500 bg-gray-50',
  };

  return (
    <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
      {responses.map((resp) => (
        <div key={resp.id} className="px-4 py-3 space-y-1">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${RESPONSE_COLORS[resp.response ?? ''] ?? 'text-gray-500 bg-gray-50'}`}
            >
              {(resp.response ?? 'unknown').replace('_', ' ')}
            </span>
            <span className="text-xs text-gray-500">
              {resp.question_text}
            </span>
          </div>
          {resp.notes && (
            <p className="text-sm text-gray-600">{resp.notes}</p>
          )}
          {resp.evidence_description && (
            <p className="text-xs text-gray-500">
              Evidence: {resp.evidence_description}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Actions tab
// ---------------------------------------------------------------------------

function ActionsTab({ clauseStatusId }: { clauseStatusId: number }) {
  const { data: actions, isLoading } = useClauseActions(clauseStatusId);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-16 rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  if (!actions || actions.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
        <ClipboardCheck className="mx-auto h-12 w-12 text-gray-300" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No corrective actions</h3>
        <p className="mt-1 text-sm text-gray-500">
          No corrective actions are linked to this clause.
        </p>
      </div>
    );
  }

  const ACTION_STATUS_COLORS: Record<string, string> = {
    open: 'text-blue-700 bg-blue-50',
    in_progress: 'text-amber-700 bg-amber-50',
    pending_verification: 'text-purple-700 bg-purple-50',
    closed: 'text-green-700 bg-green-50',
    overdue: 'text-red-700 bg-red-50',
  };

  return (
    <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
      {actions.map((action: CorrectiveAction) => (
        <Link
          key={action.id}
          to={`/compliance/actions/${action.id}`}
          className="block px-4 py-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900">{action.title}</p>
              <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                {action.description}
              </p>
            </div>
            <span
              className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_STATUS_COLORS[action.status] ?? 'text-gray-500 bg-gray-50'}`}
            >
              {action.status.replace('_', ' ')}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
            {action.assigned_to_username && (
              <span>Assigned to {action.assigned_to_username}</span>
            )}
            <span>Due {formatDateOnly(action.due_date)}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right rail
// ---------------------------------------------------------------------------

function RightRail({
  clauseId,
  clauseRef,
  status,
  manualReview,
  nextReviewDue,
  assessedAt,
  assessedByUsername,
  evidenceNotes,
  evidenceCount,
  evidenceWarning,
  evidenceItems,
  remediationHints,
  canMarkCompliant,
  onOpenEvidence,
}: {
  clauseId: number;
  clauseRef: string;
  status: ClauseStatusValue;
  manualReview: boolean;
  nextReviewDue: string | null;
  assessedAt: string | null;
  assessedByUsername: string | null;
  evidenceNotes: string | null;
  evidenceCount: number;
  evidenceWarning: string | null;
  evidenceItems: Array<{ id: number; evidence_type: string; evidence_type_display: string; title: string; collected_at: string | null }>;
  remediationHints: RemediationHint[];
  canMarkCompliant: boolean;
  onOpenEvidence: () => void;
}) {
  const timeZone = useOrgTimeZone();
  const updateClause = useUpdateClauseStatus();
  const {
    isModuleEnabled,
    isLoading: isLoadingModules,
    error: modulesError,
    modules,
  } = usePlatformModules();
  const [overrideStatus, setOverrideStatus] = useState<ClauseStatusValue | ''>('');
  const [reviewDate, setReviewDate] = useState(nextReviewDue ?? '');

  const handleOverride = () => {
    if (!overrideStatus) return;
    updateClause.mutate({ id: clauseId, data: { status: overrideStatus } });
    setOverrideStatus('');
  };

  const handleSetReviewDate = () => {
    updateClause.mutate({
      id: clauseId,
      data: { next_review_due: reviewDate || null },
    });
  };

  return (
    <div className="space-y-4">
      {/* Current status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Current Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <StatusPill status={status} manualReview={manualReview} />
          {manualReview && (
            <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
              <Eye className="mr-1 inline h-3 w-3" />
              This clause cannot be auto-assessed by the platform. A manual attestation is required.
            </div>
          )}
          {evidenceNotes && (
            <div
              className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700"
              data-testid="clause-evidence-notes"
            >
              <p className="mb-1 font-medium text-gray-900">Why this status</p>
              <p className="whitespace-pre-line">{evidenceNotes}</p>
            </div>
          )}
          <MarkCompliantButton
            clause={{ id: clauseId, clause_ref: clauseRef, can_mark_compliant: canMarkCompliant }}
          />
          {assessedAt && (
            <p className="text-xs text-gray-500">
              Assessed {formatDateTimeInTimeZone(assessedAt, timeZone, { dateStyle: 'medium' })}
              {assessedByUsername && ` by ${assessedByUsername}`}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Suggested actions */}
      {status === 'compliant' ? (
        <AdvisoryCard
          hints={remediationHints}
          clauseRef={clauseRef}
          status={status}
          onOpenEvidence={onOpenEvidence}
          isModuleEnabled={isModuleEnabled}
          isLoadingModules={isLoadingModules}
          modulesError={modulesError}
          modules={modules}
        />
      ) : (
        <SuggestedActionsCard
          hints={remediationHints}
          clauseRef={clauseRef}
          status={status}
          onOpenEvidence={onOpenEvidence}
          isModuleEnabled={isModuleEnabled}
          isLoadingModules={isLoadingModules}
          modulesError={modulesError}
          modules={modules}
        />
      )}

      {/* Evidence summary */}
      <Card data-testid="evidence-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Evidence</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">{evidenceCount}</span>{' '}
            item{evidenceCount !== 1 ? 's' : ''} linked
          </p>
          {evidenceWarning && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {evidenceWarning}
            </div>
          )}
          {evidenceItems.length > 0 && (
            <ul className="space-y-1">
              {evidenceItems.slice(0, 5).map((item) => (
                <li key={item.id} className="flex items-start gap-1.5 text-xs text-gray-600">
                  <FileText className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
                  <span className="truncate">{item.title}</span>
                  <span className="ml-auto flex-shrink-0 text-gray-400">{item.evidence_type_display}</span>
                </li>
              ))}
              {evidenceItems.length > 5 && (
                <li className="text-xs text-gray-400">+{evidenceItems.length - 5} more</li>
              )}
            </ul>
          )}
          <button
            type="button"
            onClick={onOpenEvidence}
            data-testid="evidence-card-attach"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Upload className="h-4 w-4" />
            Attach document
          </button>
        </CardContent>
      </Card>

      {/* Override status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Override Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <select
            value={overrideStatus}
            onChange={(e) => setOverrideStatus(e.target.value as ClauseStatusValue | '')}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select status…</option>
            <option value="compliant">Compliant</option>
            <option value="partially_compliant">Partially compliant</option>
            <option value="non_compliant">Non-compliant</option>
            <option value="not_applicable">Not applicable</option>
            <option value="not_assessed">Not assessed</option>
          </select>
          <button
            onClick={handleOverride}
            disabled={!overrideStatus || updateClause.isPending}
            className="w-full rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {updateClause.isPending ? 'Saving…' : 'Override'}
          </button>
        </CardContent>
      </Card>

      {/* Next review date */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Next Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <input
            type="date"
            value={reviewDate}
            onChange={(e) => setReviewDate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleSetReviewDate}
            disabled={updateClause.isPending}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Set review date
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ClauseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const clauseId = parseInt(id ?? '0', 10);
  const [activeTab, setActiveTab] = useState<TabId>('requirement');
  const handleOpenEvidence = () => setActiveTab('evidence');

  const { data: clause, isLoading, error } = useClauseStatusDetail(clauseId, clauseId > 0);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-96 rounded bg-gray-200" />
          <div className="h-64 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  if (error || !clause) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load clause details. Please try again.
        </div>
      </div>
    );
  }

  const sectionNumber = clause.clause_ref.match(/^(\d+)/)?.[1] ?? '';

  return (
    <div className="p-6 space-y-6">
      {/* Back link + header */}
      <div className="flex items-start gap-3">
        <Link
          to={sectionNumber ? `/compliance/clauses/section/${sectionNumber}` : '/compliance/clauses'}
          className="mt-1 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">
              {clause.clause_ref}
            </h1>
            <ScopeTagsBadge tags={clause.scope_tags} />
            <CriticalityPill level={clause.criticality} />
          </div>
          <p className="mt-1 text-sm text-gray-600">{clause.clause_title}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Tab bar */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex gap-4">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab content */}
          <div>
            {activeTab === 'requirement' && (
              <RequirementTab
                requirementSummary={clause.requirement_summary}
                guidanceSummary={clause.guidance_summary}
              />
            )}
            {activeTab === 'evidence' && (
              <EvidenceTab clauseStatusId={clause.id} />
            )}
            {activeTab === 'audit-history' && (
              <AuditHistoryTab clauseRef={clause.clause_ref} />
            )}
            {activeTab === 'actions' && (
              <ActionsTab clauseStatusId={clause.id} />
            )}
          </div>
        </div>

        {/* Right rail */}
        <div className="w-full lg:w-72 flex-shrink-0">
          <RightRail
            clauseId={clause.id}
            clauseRef={clause.clause_ref}
            status={clause.status}
            manualReview={needsManualReview(clause)}
            nextReviewDue={clause.next_review_due}
            assessedAt={clause.assessed_at}
            assessedByUsername={clause.assessed_by_username}
            evidenceNotes={clause.evidence_notes ?? null}
            evidenceCount={clause.evidence_count ?? 0}
            evidenceWarning={clause.evidence_warning ?? null}
            evidenceItems={clause.evidence_items ?? []}
            remediationHints={clause.remediation_hints ?? []}
            canMarkCompliant={clause.can_mark_compliant ?? false}
            onOpenEvidence={handleOpenEvidence}
          />
        </div>
      </div>
    </div>
  );
}
